import { Fragment, useEffect, useMemo, useState } from 'react'
import clsx from 'clsx'
import { RadioGroup } from '@headlessui/react'
import { useRouter } from 'next/router'

import { Icon } from '@wartek-id/icon-v2'
import { Text } from '@wartek-id/text-v1'
import { Button } from '@wartek-id/button-v1'
import { Shimmer } from '@wartek-id/shimmer'
import type { DialogProps } from '@wartek-id/dialog-v1'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogOverlay,
} from '@wartek-id/dialog-v1'
import { Collapsible, CollapsibleContent } from '@wartek-id/collapsible-v1'

import { GURU_PRAKTIK_KINERJA_INDIKATOR_IMAGE } from 'app/KinerjaGuruPage/constants/praktik-kinerja'
import { useAPIKinerjaProfile } from 'api/kinerja/useAPIKinerja'
import { PENGELOLAAN_KINERJA_GURU_PATH } from 'app/KinerjaShared/constants/path'
import {
  useAPIContentKinerjaUtama,
  useAPIKinerja,
} from 'api/kinerja/useAPIKinerja'
import { assetUrl } from 'utils/url'
import { useUpsertPerencanaan } from 'app/KinerjaGuruPage/hooks/use-upsert-perencanaan'
import useSKPTrackEvent from 'app/KinerjaPage/hooks/useSKPTrackEvent'

import type { KinerjaPraktikPembelajaranDimensi } from 'types/kinerja'
import type { UpsertSKPPayload } from 'types/kinerja'

type PraktikKinerjaProps = {
  onNext: () => void
}

export function PraktikKinerja({ onNext }: PraktikKinerjaProps) {
  const router = useRouter()
  const { trackEvent } = useSKPTrackEvent()

  const { data: profileData } = useAPIKinerjaProfile()

  // -----------------------------------------
  // Handle dimensi dialog
  // -----------------------------------------
  const [showDimensiDialog, setShowDimensiDialog] = useState(false)

  // -----------------------------------------
  // Handle indikator collapsible
  // -----------------------------------------
  const [showOtherIndikator, setShowOtherIndikator] = useState(false)

  const handleToggleOtherIndikator = () => {
    setShowOtherIndikator(!showOtherIndikator)

    // Do a top scrolling so that the other indikator content will be visible
    // upon opening the collapsible
    if (!showOtherIndikator) {
      window.scrollTo({ top: 480, behavior: 'smooth' })
    }
  }

  // -----------------------------------------
  // Get perencanaan data
  // -----------------------------------------
  const { data: perencanaanData, isLoading: isPerencanaanLoading } =
    useAPIKinerja(
      {
        staleTime: 60 * 60 * 1000, // 1 hour,
      },
      {
        tipe: 'SPF',
      }
    )

  const currentPraktikKinerja = perencanaanData?.data?.praktikPembelajaran

  // -----------------------------------------
  // Get Praktik Kinerja content
  // -----------------------------------------
  const {
    data: dataContentKinerjaUtama,
    isLoading: isLoadingContentKinerjaUtama,
  } = useAPIContentKinerjaUtama(
    {
      tipe: 'SPF',
    },
    {
      staleTime: 6 * 60 * 60 * 1000, // 6 hour
      enabled: true,
    }
  )
  const isRaporExists = dataContentKinerjaUtama?.data?.isRaporExists ?? false
  const contentPraktikPembelajaran =
    dataContentKinerjaUtama?.data?.praktikPembelajaran ?? []

  const {
    recommendedIndikatorDimensiList,
    otherIndikatorDimensiList,
  }: {
    recommendedIndikatorDimensiList: Array<Array<PraktikKinerjaItemProps>>
    otherIndikatorDimensiList: Array<Array<PraktikKinerjaItemProps>>
  } = useMemo(() => {
    // Sort dimensi by skor ascending
    const sortedDimensiList =
      dataContentKinerjaUtama?.data?.praktikPembelajaran?.sort(
        (a, b) => a.skor - b.skor
      ) || []

    let recommendedList: Array<Array<PraktikKinerjaItemProps>> = []
    let otherList: Array<Array<PraktikKinerjaItemProps>> = []

    sortedDimensiList.forEach((dimensi) => {
      const isRecommended = isRecommendedDimensi(dimensi)

      const indikatorList = dimensi.indikator.map((indikator) => {
        return {
          id: String(indikator.id),
          title: indikator.nama,
          description: indikator.definisi,
          dimensiSkor: dimensi.skor,
          dimensiNama: dimensi.dimensiNama,
          isRecommended,
        }
      })

      if (isRaporExists) {
        if (isRecommended) {
          recommendedList.push(indikatorList)
        } else {
          otherList.push(indikatorList)
        }
      } else {
        otherList.push(indikatorList)
      }
    })

    return {
      recommendedIndikatorDimensiList: recommendedList,
      otherIndikatorDimensiList: otherList,
    }
  }, [dataContentKinerjaUtama?.data?.praktikPembelajaran, isRaporExists])

  // -----------------------------------------
  // Handle form data
  // -----------------------------------------
  const [selectedPraktikKinerjaId, setSelectedPraktikKinerjaId] =
    useState<string>(undefined)

  // Sync with perencanaan data
  useEffect(() => {
    if (currentPraktikKinerja) {
      setSelectedPraktikKinerjaId(String(currentPraktikKinerja))
    }
  }, [currentPraktikKinerja])

  const isDirty = useMemo(() => {
    return selectedPraktikKinerjaId != String(currentPraktikKinerja)
  }, [currentPraktikKinerja, selectedPraktikKinerjaId])

  const isValidated = !!selectedPraktikKinerjaId

  // -----------------------------------------
  // Handle actions
  // -----------------------------------------
  const { handleUpsertMutation, isLoading: isLoadingUpsertPerencanaan } =
    useUpsertPerencanaan()
  const payload: UpsertSKPPayload = useMemo(
    () => ({
      id: perencanaanData?.data?.id,
      praktikPembelajaran: Number(selectedPraktikKinerjaId),
      tipe: 'SPF',
    }),
    [perencanaanData, selectedPraktikKinerjaId]
  )

  const handleSaveToDraft = () => {
    // Send track event
    trackEvent({
      event_name: 'CLICK_KINERJA_DRAFT_PLANNING',
      screen_name: 'KINERJA_PLANNING_PRAKTIK_PEMBELAJARAN_PAGE',
      kinerja_planning_id: perencanaanData?.data?.id,
      kinerja_planning_status: perencanaanData?.data?.status,
    })

    // Do the upsert mutation
    handleUpsertMutation(payload, {
      shouldInvalidateCache: true,
      skip: !isDirty,
      showSuccessMessage: true,
      onSuccess: () => router.push(`${PENGELOLAAN_KINERJA_GURU_PATH}`),
    })
  }

  const handleNext = () => {
    // Send track event
    trackEvent({
      event_name: 'CLICK_KINERJA_PLANNING_TO_PENGEMBANGAN_KOMPETENSI',
      screen_name: 'KINERJA_PLANNING_PRAKTIK_PEMBELAJARAN_PAGE',
      kinerja_planning_id: perencanaanData?.data?.id,
      kinerja_planning_status: perencanaanData?.data?.status,
    })

    handleUpsertMutation(payload, {
      shouldInvalidateCache: true,
      skip: !isDirty,
      onSuccess: onNext,
    })
  }
  // -----------------------------------------
  // Other derivatives
  // -----------------------------------------
  const isLoadingQueries = isPerencanaanLoading || isLoadingContentKinerjaUtama
  const isLoading = isLoadingQueries || isLoadingUpsertPerencanaan

  return (
    <>
      <div className="p-9">
        <div className="space-y-1 mb-9">
          <Text variant="heading-md" as="h3">
            Pilih 1 indikator untuk menjadi fokus peningkatan kinerja Anda
          </Text>
          {isRaporExists ? (
            <Text color="subdued">
              Kami sediakan rekomendasi berdasarkan{' '}
              <button
                className="font-bold underline hover:no-underline text-primary"
                type="button"
                onClick={() => setShowDimensiDialog(true)}
              >
                Rapor Pendidikan satuan pendidikan Anda
              </button>{' '}
              versi terakhir.
            </Text>
          ) : (
            <Text color="subdued">
              Diskusikan dengan Kepala Sekolah agar pilihan Anda turut mendukung
              peningkatan kinerja satuan pendidikan.
            </Text>
          )}
        </div>

        {isRaporExists && (
          <div className="flex items-center gap-5 p-4 mb-3">
            <Text variant="action">
              Kualitas
              <br />
              Pembelajaran
            </Text>
            <div className="border-r border-gray-20 h-16">&nbsp;</div>
            <div className="space-y-1">
              <Text color="subdued">Dimensi Prioritas:</Text>
              <Text color="subdued">
                {contentPraktikPembelajaran
                  .filter(isRecommendedDimensi)
                  .sort((a, b) => a.dimensiId.localeCompare(b.dimensiId))
                  .map((e) => e.dimensiNama)
                  .join(', ') || 'Tidak ada. Pilih sesuai kebutuhan Anda.'}
              </Text>
            </div>
          </div>
        )}

        {isLoading ? (
          <div className="space-y-6">
            <Shimmer height="160px" width="100%" />
            <Shimmer height="160px" width="100%" />
            <Shimmer height="160px" width="100%" />
            <Shimmer height="160px" width="100%" />
            <Shimmer height="160px" width="100%" />
          </div>
        ) : (
          <RadioGroup
            onChange={setSelectedPraktikKinerjaId}
            value={selectedPraktikKinerjaId}
          >
            <div className="max-w-5xl">
              {isRaporExists && recommendedIndikatorDimensiList.length > 0 ? (
                // If isRaporExists is true show both recommended indikator and other indikator (collapsible)
                <>
                  {/* Recommended indikator */}
                  <div className="space-y-6">
                    {recommendedIndikatorDimensiList.map(
                      (indikatorGroup, groupIndex) => (
                        <div key={groupIndex}>
                          <div className="grid grid-cols-3 gap-6">
                            {indikatorGroup.map((item) => (
                              <PraktikKinerjaItem {...item} key={item.id} />
                            ))}
                          </div>
                        </div>
                      )
                    )}
                  </div>
                  <Collapsible
                    className="!border-none my-6"
                    open={showOtherIndikator}
                    onToggle={handleToggleOtherIndikator}
                  >
                    <button
                      className="flex gap-1 items-center"
                      type="button"
                      aria-expanded={showOtherIndikator}
                      aria-controls="other-indikator-content"
                      onClick={handleToggleOtherIndikator}
                    >
                      <Text variant="action" color="primary" as="span">
                        {showOtherIndikator ? 'Sembunyikan' : 'Tampilkan'}{' '}
                        indikator lainnya
                      </Text>
                      <Icon
                        className={clsx(
                          showOtherIndikator && 'rotate-180',
                          'transition-transform duration-600 transition-delay-100'
                        )}
                      >
                        expand_more
                      </Icon>
                    </button>
                    <CollapsibleContent>
                      <div id="other-indikator-content" className="pt-4">
                        {/* Other indikator */}
                        <div className="space-y-6">
                          {otherIndikatorDimensiList.map(
                            (indikatorGroup, groupIndex) => (
                              <div
                                className="grid grid-cols-3 gap-6"
                                key={groupIndex}
                              >
                                {indikatorGroup.map((item) => (
                                  <PraktikKinerjaItem {...item} key={item.id} />
                                ))}
                              </div>
                            )
                          )}
                        </div>
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                </>
              ) : (
                // If isRaporExists is false, show only other indikator
                <div className="space-y-6">
                  {otherIndikatorDimensiList.map(
                    (indikatorGroup, groupIndex) => (
                      <div className="grid grid-cols-3 gap-6" key={groupIndex}>
                        {indikatorGroup.map((item) => (
                          <PraktikKinerjaItem {...item} key={item.id} />
                        ))}
                      </div>
                    )
                  )}
                </div>
              )}
            </div>
          </RadioGroup>
        )}
      </div>
      <div className="border-t border-subdued px-6 py-4 flex justify-end gap-5">
        <Button
          size="lg"
          color="white"
          onClick={handleSaveToDraft}
          disabled={isPerencanaanLoading || !isDirty}
        >
          Simpan draf
        </Button>

        <Button
          color="black"
          size="lg"
          disabled={
            isPerencanaanLoading || isLoadingUpsertPerencanaan || !isValidated
          }
          onClick={handleNext}
        >
          Ke Pengembangan Kompetensi
        </Button>
      </div>

      <DialogDimensiDetail
        size="lg"
        isOpen={showDimensiDialog}
        onClose={() => setShowDimensiDialog(false)}
        contentPraktikPembelajaran={
          dataContentKinerjaUtama?.data?.praktikPembelajaran || []
        }
        satuanPendidikan={profileData?.data?.satuanPendidikan || '-'}
      />
    </>
  )
}

function isRecommendedDimensi(dimensi: KinerjaPraktikPembelajaranDimensi) {
  return dimensi.skor === 1
}

type PraktikKinerjaItemProps = {
  id: string
  title: string
  description: string
  dimensiSkor: number
  dimensiNama: string
  isRecommended: boolean
}

const DIMENSI_SKOR_BADGE_STYLES: Record<number, string> = {
  1: 'bg-surface-critical-subdued text-critical',
  2: 'bg-surface-warning-subdued text-yellow-700',
  3: 'bg-surface-success-subdued text-success',
}

function PraktikKinerjaItem({
  id,
  title,
  description,
  dimensiSkor,
  dimensiNama,
  isRecommended,
}: PraktikKinerjaItemProps) {
  const labelElementId = 'praktik-kinerja-item-label-' + id

  const badgeStyle =
    DIMENSI_SKOR_BADGE_STYLES[dimensiSkor] ??
    'bg-surface-informational-subdued text-blue-50'

  return (
    <RadioGroup.Option value={id} as={Fragment}>
      {({ checked }) => (
        <div
          className={clsx(
            'rounded border pt-10 pb-4 px-4 outline-none cursor-pointer relative',
            'flex flex-col gap-4 justify-between items-start',
            checked
              ? 'bg-surface-selected-default border-blue-60'
              : 'bg-white border-subdued'
          )}
          aria-labelledby={labelElementId}
        >
          {isRecommended && (
            <div className="p-2 bg-surface-informational-subdued rounded-tr rounded-bl absolute top-0 right-0">
              <Text variant="body-sm-bold" className="!text-blue-60">
                Rekomendasi
              </Text>
            </div>
          )}
          <div className="space-y-2">
            <img
              className="flex-shrink-0 flex-grow-0"
              src={assetUrl(
                `/illustrations/${GURU_PRAKTIK_KINERJA_INDIKATOR_IMAGE[title]}`
              )}
              alt=""
              height={84}
              width={84}
            />

            <Text variant="heading-sm" id={labelElementId}>
              {title}
            </Text>
            <Text variant="body" color="subdued">
              {description}
            </Text>
          </div>
          <div className={clsx('px-2 py-1 rounded', badgeStyle)}>
            <Text variant="body-sm" color="inherit">
              Dimensi: {dimensiNama}
            </Text>
          </div>
        </div>
      )}
    </RadioGroup.Option>
  )
}

interface DialogDimensiDetailProps extends Omit<DialogProps, 'children'> {
  contentPraktikPembelajaran: KinerjaPraktikPembelajaranDimensi[]
  satuanPendidikan: string
}

const getDimensiStyle = (skor: number) => {
  switch (skor) {
    case 1:
      return {
        value: 'Kurang',
        color: 'bg-red-50',
      }
    case 2:
      return {
        value: 'Cukup',
        color: 'bg-orange-40',
      }
    default:
      return {
        value: 'Baik',
        color: 'bg-green-50',
      }
  }
}

export default function DialogDimensiDetail({
  contentPraktikPembelajaran,
  satuanPendidikan,
  ...props
}: DialogDimensiDetailProps) {
  const dimensiList = useMemo(() => {
    return [
      {
        label: 'D.1.1 Manajemen Kelas',
        description: `Pengelolaan kelas yang mendukung pembelajaran serta penerapan penghargaan dan sanksi secara proporsional.`,
        ...getDimensiStyle(
          contentPraktikPembelajaran.find(
            (dimensi) => dimensi.dimensiNama === 'Manajemen Kelas'
          )?.skor
        ),
      },
      {
        label: 'D.1.2 Dukungan Psikologis',
        description: `Praktik pembelajaran yang memenuhi kebutuhan psikologis siswa untuk menumbuhkan kepercayaan diri dan perasaan diterima tanpa dibeda-bedakan.`,
        ...getDimensiStyle(
          contentPraktikPembelajaran.find(
            (dimensi) => dimensi.dimensiNama === 'Dukungan Psikologis'
          )?.skor
        ),
      },
      {
        label: 'D.1.3 Metode Pembelajaran',
        description: `Praktik pembelajaran interaktif yang sesuai dengan tujuan pembelajaran dan karakteristik siswa.`,
        ...getDimensiStyle(
          contentPraktikPembelajaran.find(
            (dimensi) => dimensi.dimensiNama === 'Metode Pembelajaran'
          )?.skor
        ),
      },
    ]
  }, [contentPraktikPembelajaran])

  return (
    <Dialog {...props}>
      <DialogOverlay />
      <DialogContent aria-labelledby="dialog-dimensi-detail-title">
        <DialogHeader className="flex gap-4 items-center justify-between">
          <div className="space-y-2">
            <Text
              variant="subheading"
              color="subdued"
              className="uppercase"
              as="h3"
              id="dialog-dimensi-detail-title"
            >
              Rapor Pendidikan Satuan Pendidikan Anda
            </Text>
            <Text variant="heading-md">{satuanPendidikan}</Text>
          </div>
          <Button
            size="md"
            color="white"
            as="a"
            href="https://raporpendidikan.kemdikbud.go.id/download-report"
            target="_blank"
            rel="noreferrer"
            type={undefined}
            className="flex items-center"
          >
            Ke sistem Rapor Pendidikan
            <Icon>keyboard_arrow_right</Icon>
          </Button>
        </DialogHeader>

        <div className="p-6 space-y-8 max-w-3xl">
          <section className="flex items-center gap-5">
            <img
              className="w-16 h-16 flex-shrink-0 flex-grow-0"
              alt=""
              src={assetUrl('/illustrations/indicator-illustration.svg')}
              width={64}
              height={64}
            />
            <Text variant="action">Kualitas Pembelajaran</Text>
            <div className="border-r border-gray-20 h-16">&nbsp;</div>
            <Text color="subdued">
              Kualitas pengelolaan kelas dan penyelenggaraan pembelajaran
              interaktif yang sesuai dengan tujuan pembelajaran dan
              karakteristik siswa.
            </Text>
          </section>

          <section className="space-y-4">
            <Text variant="heading-sm">Dimensi</Text>

            <ul className="border border-subdued rounded">
              {dimensiList.map((dimensi, index) => (
                <li
                  key={index}
                  className="flex items-center gap-4 p-3 border-b last:border-b-0 border-subdued"
                >
                  <div className="space-y-1">
                    <Text variant="action">{dimensi.label}</Text>
                    <Text variant="body-sm" color="subdued">
                      {dimensi.description}
                    </Text>
                  </div>

                  <div
                    className={clsx('px-6 py-2 rounded-full', dimensi.color)}
                  >
                    <Text color="inverse" variant="body-bold">
                      {dimensi.value}
                    </Text>
                  </div>
                </li>
              ))}
            </ul>

            <div className="p-4 rounded bg-surface-informational-subdued">
              <Text>
                <strong>Catatan</strong>: Anda dapat berdiskusi dengan atasan
                untuk mempelajari Rapor Pendidikan Satdik Anda atau pun info
                lainnya tentang tahap Perencanaan.
              </Text>
            </div>
          </section>

          <Button
            size="lg"
            onClick={() => props.onClose?.()}
            className="mx-auto"
          >
            Saya mengerti
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
